import { auth } from '../auth/FirebaseAuth';
import { logDev } from '../utils/LogDev';

export class ApiManager {
    constructor() { }

    private getApiUrl() {
        return (import.meta as any).env.VITE_API_URL || '';
    }

    async uploadSprite(projectId: string, name: string, sheet: string, base64: string, metadata: any) {
        const user = auth.currentUser;
        const idToken = await user?.getIdToken();

        try {
            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(projectId)}/sprites/${encodeURIComponent(name)}/sheets/${encodeURIComponent(sheet)}`;
            logDev(`[API] POST uploadSprite: ${url}`, { metadata });
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idToken,
                    name,
                    sheet,
                    imageBase64: base64,
                    metadata
                })
            });

            const json = await res.json();
            if (json.error) {
                logDev(`[API] uploadSprite ERROR:`, json.error);
                return { success: false, error: json.error };
            }
            logDev(`[API] uploadSprite SUCCESS:`, json.filename);
            return { success: true, filename: json.filename };
        } catch (err: any) {
            return { success: false, error: err.message || 'Unknown Error' };
        }
    }

    async renameSheet(projectId: string, spriteName: string, oldSheet: string, newSheetName: string): Promise<boolean> {
        if (!spriteName || !newSheetName) return false;

        try {
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();

            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(projectId)}/sprites/${encodeURIComponent(spriteName)}/sheets/${encodeURIComponent(oldSheet)}/rename`;
            logDev(`[API] PUT renameSheet: ${url}`, { newSheetName });

            const res = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, newName: newSheetName })
            });

            if (!res.ok) {
                const err = await res.json();
                logDev(`[API] renameSheet ERROR:`, err.error);
                throw new Error(err.error || 'Failed to rename sheet');
            }
            logDev(`[API] renameSheet SUCCESS`);
            return true;
        } catch (e: any) {
            alert(e.message);
            return false;
        }
    }

    async renameSprite(projectId: string, oldName: string, newName: string): Promise<boolean> {
        if (!newName || !oldName) return false;

        try {
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();

            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(projectId)}/sprites/${encodeURIComponent(oldName)}/rename`;
            logDev(`[API] PUT renameSprite: ${url}`, { newName });

            const res = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, newName })
            });

            if (!res.ok) {
                const err = await res.json();
                logDev(`[API] renameSprite ERROR:`, err.error);
                throw new Error(err.error || 'Failed to rename sprite');
            }
            logDev(`[API] renameSprite SUCCESS`);
            return true;
        } catch (e: any) {
            alert(e.message);
            return false;
        }
    }

    async deleteSprite(projectId: string, spriteName: string): Promise<boolean> {
        if (!spriteName) return false;
        try {
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();

            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(projectId)}/sprites/${encodeURIComponent(spriteName)}`;
            logDev(`[API] DELETE deleteSprite: ${url}`);

            const res = await fetch(url, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
            });

            if (!res.ok) {
                logDev(`[API] deleteSprite ERROR`);
                throw new Error('Failed to delete sprite');
            }
            logDev(`[API] deleteSprite SUCCESS`);
            return true;
        } catch (e: any) {
            console.error("Delete Sprite Failed", e);
            alert(e.message);
            return false;
        }
    }

    async deleteSheet(projectId: string, spriteName: string, sheetName: string): Promise<boolean> {
        if (!spriteName || !sheetName) return false;

        try {
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();

            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(projectId)}/sprites/${encodeURIComponent(spriteName)}/sheets/${encodeURIComponent(sheetName)}`;
            logDev(`[API] DELETE deleteSheet: ${url}`);

            const res = await fetch(url, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
            });

            if (!res.ok) {
                logDev(`[API] deleteSheet ERROR`);
                throw new Error('Failed to delete sheet');
            }
            logDev(`[API] deleteSheet SUCCESS`);
            return true;
        } catch (e: any) {
            console.error("Delete Sheet Failed", e);
            alert(e.message);
            return false;
        }
    }

    async renameProject(oldName: string, newName: string): Promise<string | null> {
        try {
            const user = auth.currentUser;
            if (!user) return null;
            const idToken = await user.getIdToken();

            const url = `${this.getApiUrl()}/api/sprite-lab/projects/${encodeURIComponent(oldName)}/rename`;
            logDev(`[API] PUT renameProject: ${url}`, { newName });

            const res = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, newName })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to rename project');
            }

            const data = await res.json();
            logDev(`[API] renameProject SUCCESS:`, data.newName);
            return data.newName;
        } catch (e: any) {
            alert(e.message);
            return null;
        }
    }
}
